import { useState, useCallback, useEffect, useRef } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import type {
  HnpUploadTab,
  OnboardPatientStatusResponse,
} from '../../services/api/types.gen';
import { getTodayInPT } from '../../utils/dateUtils';
import { getReferralDocs } from '../../services/api';
import {
  getOnboardPatientStatusKinnserPatientOnboardStatusIntakeIdGet,
  startOnboardPatientKinnserPatientOnboardAsyncClinicIdBranchIdPost,
} from '../../services/api/sdk.gen';
import { useToast } from '../useToastContext';
import {
  extractDetailMessage,
  getResponseDetail,
  getSurfaceableError,
  parseFieldErrorsFromDetail,
  type FieldValidationError,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { resolveClinicKeyFromAgencyClinicId } from '../../utils/clinicKeyUtils';
import { isPushTerminalIntakeStatus } from '../../constants/intakeStatus';
import { useStore } from '../../store';

export interface KinnserAttachment {
  client_file: string | null;
  server_file: string | null;
  error: string;
}

export type KinnserOnboardValidationError = FieldValidationError;

export interface KinnserOnboardPatientResponse {
  patient_creation: {
    workflow_id: string;
    success: boolean;
    message: string;
  };
  task_creation: {
    patient_task_key: string;
    task_name: string;
  };
  upload: {
    attachments: KinnserAttachment[];
    patient_task_key: string;
  };
  // Per-field validation errors from the backend (HTTP 400 with detail.errors).
  // Used to render clickable bullets that jump to the offending field.
  validation_errors?: KinnserOnboardValidationError[];
  // Set when the failure carries a surfaceable business error (HTTP 409). The
  // display gates generic-message suppression on this so the real message shows.
  surfaceable?: boolean;
  error_code?: string;
}

/**
 * Build a "push to EMR failed" response envelope, keeping placeholder values
 * for the fields we never got a chance to populate. Used by every error path
 * so the UI can render a consistent failure state.
 */
function buildFailedOnboardResult(args: {
  message: string;
  taskName: string;
  validationErrors?: KinnserOnboardValidationError[];
  surfaceable?: boolean;
  errorCode?: string;
}): KinnserOnboardPatientResponse {
  const { message, taskName, validationErrors, surfaceable, errorCode } = args;
  return {
    patient_creation: { workflow_id: '', success: false, message },
    task_creation: { patient_task_key: '', task_name: taskName },
    upload: { attachments: [], patient_task_key: '' },
    ...(validationErrors ? { validation_errors: validationErrors } : {}),
    ...(surfaceable ? { surfaceable: true, error_code: errorCode } : {}),
  };
}

// Exact string CreateEpisodeStep matches to suppress the generic failure box
// once push_failed is already rendered from the persisted intake status.
// Exported so the suppression sites import it instead of hand-syncing copies.
export const GENERIC_ONBOARD_FAILURE_MESSAGE =
  'Failed to onboard patient. Please try again.';

const POLL_INTERVAL_MS = 5000;
// ~15 minutes at 5s per attempt before giving up on the client (the workflow
// keeps running server-side; polling resumes on reload via the stored id).
const MAX_POLL_ATTEMPTS = 180;

/**
 * Envelope for a completed onboard whose result payload is unavailable (e.g.
 * the intake is already pushed but the workflow history was garbage-collected,
 * so the status route returns `result: null`) — render the generic success state.
 */
function buildGenericSuccessResult(
  taskName: string
): KinnserOnboardPatientResponse {
  return {
    patient_creation: {
      workflow_id: '',
      success: true,
      message: 'Patient successfully onboarded in Kinnser',
    },
    task_creation: { patient_task_key: '', task_name: taskName },
    upload: { attachments: [], patient_task_key: '' },
  };
}

export interface S3File {
  url: string;
  filename: string;
}

interface ReferralDocument {
  id: string;
  file_name: string;
  url: string;
}

interface UseKinnserOnboardingOptions<
  T extends {
    agency_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
> {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  /**
   * Stored Temporal workflow id from a previously started onboard
   * (`intake.kinnser_onboard_workflow_id`). When set and the intake status is
   * non-terminal, polling resumes automatically after a page reload.
   */
  kinnserOnboardWorkflowId?: string | null;
  trigger: UseFormTrigger<T>;
  getValues: UseFormGetValues<T>;
  tabName: HnpUploadTab | null | undefined;
  taskName: string | null | undefined;
  s3Files: S3File[];
  // Optional task/upload configuration
  taskDate?: string; // YYYY-MM-DD format
  patientLocation?: string;
}

interface UseKinnserOnboardingReturn {
  onboardingInKinnser: boolean;
  onboardResult: KinnserOnboardPatientResponse | null;
  handleOnboardInKinnser: () => Promise<void>;
}

/**
 * Hook for onboarding patients in Kinnser (creates patient, task, and uploads attachment).
 * Manages the onboarding state and handles the API call.
 */
export function useKinnserOnboarding<
  T extends {
    agency_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
>({
  intakeId,
  trigger,
  getValues,
  tabName,
  taskName,
  s3Files,
  taskDate,
  patientLocation,
  intakeStatus,
  kinnserOnboardWorkflowId,
}: UseKinnserOnboardingOptions<T>): UseKinnserOnboardingReturn {
  const [creatingInKinnser, setOnboardingInKinnser] = useState(false);
  const [onboardResult, setOnboardResult] =
    useState<KinnserOnboardPatientResponse | null>(null);
  // Destructured so poll callbacks depend on the stable store function rather
  // than the object literal useToast() rebuilds every render.
  const { success: showSuccessToast } = useToast();

  // Guard every await: stop polling and skip state updates after unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Prevents overlapping poll loops (resume-on-mount vs a fresh start).
  const pollingRef = useRef(false);

  const clearOnboardResult = useCallback(() => {
    setOnboardResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearOnboardResult
  );

  /**
   * Poll the onboard status route until a terminal state (or give-up).
   * Keeps `onboardingInKinnser` true for the whole poll so the button stays
   * disabled, and releases it in `finally`.
   */
  const pollOnboardStatus = useCallback(async (): Promise<void> => {
    if (!intakeId || pollingRef.current) return;
    pollingRef.current = true;
    setOnboardingInKinnser(true);
    try {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (!isMountedRef.current) return;

        let status: OnboardPatientStatusResponse | undefined;
        try {
          const statusResponse =
            await getOnboardPatientStatusKinnserPatientOnboardStatusIntakeIdGet(
              {
                path: { intake_id: intakeId },
                throwOnError: true,
              }
            );
          status = statusResponse.data;
        } catch (error) {
          // A failed status fetch means the state was NOT observed — treat it
          // like the server's `unknown` and keep polling.
          console.error('Error fetching Kinnser onboard status:', error);
        }
        if (!isMountedRef.current) return;

        if (status?.status === 'completed') {
          setOnboardResult(
            status.result
              ? (status.result as KinnserOnboardPatientResponse)
              : buildGenericSuccessResult(taskName ?? '')
          );
          return;
        }

        if (status?.status === 'not_started') {
          // The server cleared the stored workflow id: the workflow was never
          // started (lost start response) or its history aged out of Temporal
          // retention. Stop polling and re-enable the push button — a fresh
          // push safely reconciles (an already-onboarded patient surfaces
          // PATIENT_ALREADY_EXISTS and routes to the add-admission flow).
          return;
        }

        if (status?.status === 'failed') {
          if (status.surfaceable && status.error_code) {
            // Surfaceable business error — show the server message verbatim.
            setOnboardResult(
              buildFailedOnboardResult({
                message: status.error || 'Onboarding failed',
                taskName: taskName ?? '',
                surfaceable: true,
                errorCode: status.error_code,
              })
            );
          } else {
            setOnboardResult(
              buildFailedOnboardResult({
                message: GENERIC_ONBOARD_FAILURE_MESSAGE,
                taskName: taskName ?? '',
              })
            );
          }
          return;
        }

        // processing / unknown / not yet visible: keep polling.
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      if (!isMountedRef.current) return;
      // Give-up is NOT a failure: the workflow keeps running server-side and
      // polling resumes on reload via the stored workflow id.
      showSuccessToast(
        'Patient onboarding is still processing in WellSky — check back shortly.'
      );
    } finally {
      pollingRef.current = false;
      if (isMountedRef.current) {
        setOnboardingInKinnser(false);
      }
    }
  }, [intakeId, taskName, showSuccessToast]);

  // Resume polling after a page reload while an onboard is in flight. At most
  // once per intake per mount — a resolved poll must not restart when a
  // re-render re-fires this effect (e.g. after a surfaceable failure that
  // leaves the intake status non-terminal). The shared terminal set includes
  // rejected/duplicate/expired so a dead intake with a stale workflow id
  // never auto-polls (which would disable the button and, worst case, let a
  // late-completing workflow overwrite the rejection server-side).
  const resumeAttemptedForIntakeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!intakeId || !kinnserOnboardWorkflowId) return;
    if (isPushTerminalIntakeStatus(intakeStatus) || pollingRef.current) return;
    if (resumeAttemptedForIntakeRef.current === intakeId) return;
    resumeAttemptedForIntakeRef.current = intakeId;
    void pollOnboardStatus();
  }, [intakeId, kinnserOnboardWorkflowId, intakeStatus, pollOnboardStatus]);

  const handleOnboardInKinnser = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    // Validate that if any H&P field is specified, all must be specified
    const tabNameProvided = tabName !== null && tabName !== undefined;
    const taskNameProvided =
      taskName !== null && taskName !== undefined && taskName.trim() !== '';
    const taskDateProvided =
      taskDate !== null && taskDate !== undefined && taskDate.trim() !== '';

    // If tab_name and task_name are provided but task_date is not, default to today
    let taskDateValue: string | null = taskDate || null;
    if (tabNameProvided && taskNameProvided && !taskDateProvided) {
      taskDateValue = getTodayInPT(); // YYYY-MM-DD format in Pacific Time
    }

    // Count how many fields are provided (after defaulting task_date if needed)
    const finalTaskDateProvided = taskDateValue !== null;
    const providedCount = [
      tabNameProvided,
      taskNameProvided,
      finalTaskDateProvided,
    ].filter(Boolean).length;

    // If any field is provided, all must be provided
    if (providedCount > 0 && providedCount < 3) {
      setOnboardResult(
        buildFailedOnboardResult({
          message:
            'If any of H&P upload tab, H&P upload task, or task date is specified, all must be specified.',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    // Get clinic_id and branch_id from form values
    const formValues = getValues();
    const agencyClinicId = formValues.agency_clinic_id;
    const branchId = formValues.kinnser_branch_id;

    if (!agencyClinicId || !branchId) {
      console.error('Clinic ID and Branch ID are required for onboarding');
      setOnboardResult(
        buildFailedOnboardResult({
          message: 'Clinic ID and Branch ID are required',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    // Resolve Kinnser clinic_key from the agency_clinic_id (UUID) stored in the form
    const agencyClinics = useStore.getState().agencyClinics;
    const clinicKey = resolveClinicKeyFromAgencyClinicId(
      agencyClinics,
      agencyClinicId
    );

    if (!clinicKey) {
      console.error(
        'Could not resolve Kinnser clinic_key for agency_clinic_id:',
        agencyClinicId
      );
      setOnboardResult(
        buildFailedOnboardResult({
          message:
            'Could not resolve Kinnser clinic key. Please ensure the selected clinic has a valid Kinnser configuration.',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    const clinicIdNum = parseInt(clinicKey, 10);
    const branchIdNum = parseInt(branchId, 10);
    if (Number.isNaN(clinicIdNum) || Number.isNaN(branchIdNum)) {
      console.error(
        'Invalid Kinnser clinic or branch id for onboard path:',
        clinicKey,
        branchId
      );
      setOnboardResult(
        buildFailedOnboardResult({
          message: 'Clinic ID and Branch ID are required',
          taskName: taskName ?? '',
        })
      );
      return;
    }

    setOnboardingInKinnser(true);
    setOnboardResult(null);

    try {
      // Validate that we have at least one file
      if (!s3Files || s3Files.length === 0) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: 'At least one document is required for onboarding',
            taskName: taskName ?? '',
          })
        );
        setOnboardingInKinnser(false);
        return;
      }

      // Regenerate presigned URLs to ensure they haven't expired
      // This prevents 403 errors when the user has been on the page for a long time
      let freshS3Files: S3File[] = [];
      try {
        const urlResponse = await getReferralDocs({
          path: { intake_id: intakeId },
        });
        const documents = urlResponse.data?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          freshS3Files = (documents as unknown as ReferralDocument[]).map(
            doc => ({
              url: doc.url,
              filename: doc.file_name,
            })
          );
        } else {
          console.error('Failed to get fresh presigned URLs');
          setOnboardResult(
            buildFailedOnboardResult({
              message: 'Failed to generate presigned URLs for documents',
              taskName: taskName ?? '',
            })
          );
          setOnboardingInKinnser(false);
          return;
        }
      } catch (urlError) {
        console.error('Error fetching fresh presigned URLs:', urlError);
        setOnboardResult(
          buildFailedOnboardResult({
            message: 'Failed to generate presigned URLs for documents',
            taskName: taskName ?? '',
          })
        );
        setOnboardingInKinnser(false);
        return;
      }

      // Start the onboard workflow (returns immediately), then poll the
      // status route until the workflow reaches a terminal state (EMR-185).
      const startResponse =
        await startOnboardPatientKinnserPatientOnboardAsyncClinicIdBranchIdPost(
          {
            path: { clinic_id: clinicIdNum, branch_id: branchIdNum },
            body: {
              intake_id: intakeId,
              tab_name: tabName || null,
              task_name: taskName || null,
              task_date: taskDateValue || null,
              patient_location: patientLocation || null,
              s3_files: freshS3Files,
            },
            throwOnError: true,
          }
        );
      if (!isMountedRef.current) {
        return;
      }

      if (!startResponse.data?.workflow_id) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: GENERIC_ONBOARD_FAILURE_MESSAGE,
            taskName: taskName ?? '',
          })
        );
        return;
      }

      await pollOnboardStatus();
    } catch (error) {
      console.error('Error onboarding patient in Kinnser:', error);
      if (!isMountedRef.current) {
        return;
      }

      // Surfaceable business errors (HTTP 409) take priority and are shown verbatim.
      const surfaceable = getSurfaceableError(error);
      if (surfaceable) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: surfaceable.message,
            taskName: taskName ?? '',
            surfaceable: true,
            errorCode: surfaceable.errorCode,
          })
        );
        return;
      }

      const status = getHttpStatus(error);
      const is4xx = status !== undefined && status >= 400 && status < 500;
      const detail = getResponseDetail(error);

      const validationErrors = is4xx
        ? parseFieldErrorsFromDetail(detail)
        : undefined;
      if (validationErrors && validationErrors.length > 0) {
        setOnboardResult(
          buildFailedOnboardResult({
            message:
              'Some required fields are missing or invalid. Update them and try again.',
            taskName: taskName ?? '',
            validationErrors,
          })
        );
        return;
      }

      const detailMessage = is4xx ? extractDetailMessage(detail) : null;
      if (detailMessage) {
        setOnboardResult(
          buildFailedOnboardResult({
            message: detailMessage,
            taskName: taskName ?? '',
          })
        );
        return;
      }

      setOnboardResult(
        buildFailedOnboardResult({
          message: GENERIC_ONBOARD_FAILURE_MESSAGE,
          taskName: taskName ?? '',
        })
      );
    } finally {
      if (isMountedRef.current) {
        setOnboardingInKinnser(false);
      }
    }
  }, [
    intakeId,
    trigger,
    getValues,
    tabName,
    taskName,
    taskDate,
    patientLocation,
    s3Files,
    pollOnboardStatus,
  ]);

  return {
    onboardingInKinnser: creatingInKinnser,
    onboardResult: onboardResult,
    handleOnboardInKinnser: handleOnboardInKinnser,
  };
}
